import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import styles from "./Style.module.css";
// import DropDownIcon from "../../Icons/DropDownIcon";
import SearchBar from "../../Search/SearchBar";
import useKeyboardNavigation from "../../../Utils/Hooks/useKeyboardNavigation";
import { getServiceAddonsList } from "../../../APICalling/APICalling";
import { getEffectiveAddonPrice } from "../../../Utils/priceRateHelpers";

const ServiceAddons = ({
	options,
	onChange,
	selectedValues,
	onDropdownToggle = null,
	serviceId,
	outletId,
	isMembershipApplied = false,
	publicHoliday = false,
	selectedDate,
	serviceStartDate,
	serviceStartTime,
	readOnly = false,
}) => {
	const [isDropdownOpen, setDropdownOpen] = useState(false);
	const [searchQuery, setSearchQuery] = useState("");
	const [addonsList, setAddonsList] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [apiError, setApiError] = useState(null);
	const [emptyListMessage, setEmptyListMessage] = useState(null);

	const dropdownRef = useRef(null);
	const dropdownMenuRef = useRef(null);
	const searchInputRef = useRef(null);

	// Use fetched options when local options prop is not provided
	const effectiveOptions = useMemo(
		() => options || addonsList,
		[options, addonsList]
	);

	// Normalise selected values (controlled)
	const selectedAddons = useMemo(() => {
		if (!Array.isArray(selectedValues) || selectedValues.length === 0) {
			return [];
		}
		// Detect if array is of ids or objects
		if (typeof selectedValues[0] === "object") {
			return selectedValues;
		}
		// Treat as array of ids
		const idSet = new Set(selectedValues);
		const allItems = [];
		effectiveOptions.forEach((cat) => {
			(cat?.items || cat?.addons || []).forEach((item) =>
				allItems.push(item)
			);
		});
		return allItems.filter((item) =>
			idSet.has(item.id || item.addon_id || item.item_id)
		);
	}, [selectedValues, effectiveOptions]);






	const fetchAddons = useCallback(async () => {
		if (options || addonsList.length > 0 || !serviceId || !outletId) {
			return;
		}
		
		try {
			setIsLoading(true);
			setApiError(null);
			setEmptyListMessage(null);

			const params = { 
				service_id: serviceId, 
				outlet_id: outletId, 
				status: 1
			};
			const response = await getServiceAddonsList(params);
			const payload = response?.data;
			const addons = payload?.data || [];
			const allCategoryAddonsEmpty =
				addons.length > 0 &&
				addons.every(
					(cat) =>
						(cat?.items || cat?.addons || []).length === 0
				);
			setAddonsList(addons);
			setEmptyListMessage(
				(addons.length === 0 || allCategoryAddonsEmpty) &&
					typeof payload?.message === "string"
					? payload.message
					: null
			);

		} catch (error) {
			console.error("Error fetching add-ons:", error);
			setApiError("Failed to load add-ons");
		} finally {
			setIsLoading(false);
		}
	}, [serviceId, options, addonsList.length, outletId]);

	// Resolve id-only selections to labels when viewing a completed booking (no dropdown)
	useEffect(() => {
		if (!readOnly || !serviceId || !outletId || options) return;
		fetchAddons();
	}, [readOnly, serviceId, outletId, options, fetchAddons]);

	const addonPricingContext = useMemo(
		() => ({
			isMember: isMembershipApplied,
			publicHoliday,
			selectedDate: serviceStartDate || selectedDate,
			serviceTime: serviceStartTime
				? String(serviceStartTime).slice(0, 5)
				: "",
		}),
		[
			isMembershipApplied,
			publicHoliday,
			selectedDate,
			serviceStartDate,
			serviceStartTime,
		]
	);

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target)
			) {
				setDropdownOpen(false);
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	// Auto-scroll dropdown into view inside scrollable parent
	useEffect(() => {
		if (isDropdownOpen && dropdownMenuRef.current) {
			dropdownMenuRef.current.scrollIntoView({
				behavior: "smooth",
				block: "nearest",
			});
		}
	}, [isDropdownOpen]);

	// Focus search input when dropdown opens
	useEffect(() => {
		if (isDropdownOpen && searchInputRef.current) {
			setTimeout(() => {
				searchInputRef.current?.focus();
				searchInputRef.current?.select();
			}, 0);
		}
	}, [isDropdownOpen]);

	// Filter options based on search query
	const filteredOptions = useMemo(() => {
		const src = effectiveOptions || [];
		return src
			.map((category) => {
				const sourceItems = category?.items || category?.addons || [];
				const items =
					sourceItems.filter((item) =>
						(item?.name || item?.addon_name || item?.item_name || "")
							.toLowerCase()
							.includes(searchQuery.toLowerCase())
					) || [];

				const categoryName =
					category?.name ||
					category?.category_name ||
					category?.title ||
					"";
				const categoryMatches = categoryName
					.toLowerCase()
					.includes(searchQuery.toLowerCase());

				if (categoryMatches || items.length > 0) {
					return {
						...category,
						items: categoryMatches ? category.items : items,
					};
				}
				return null;
			})
			.filter(Boolean);
	}, [effectiveOptions, searchQuery]);

	const emptyDropdownPlaceholder = useMemo(() => {
		const hasItems = (effectiveOptions || []).some(
			(cat) => (cat?.items || cat?.addons || []).length > 0
		);
		if (searchQuery.trim() && hasItems) {
			return "No matching add-ons";
		}
		return emptyListMessage || "No add-ons found";
	}, [effectiveOptions, searchQuery, emptyListMessage]);

	// Flatten visible items for keyboard navigation
	const visibleItems = useMemo(() => {
		const items = [];
		filteredOptions.forEach((category) => {
			const sourceItems = category?.items || category?.addons || [];
			if (sourceItems.length > 0) {
				sourceItems.forEach((item) => {
					items.push(item);
				});
			}
		});
		return items;
	}, [filteredOptions]);

	// Keyboard navigation
	const {
		handleKeyDown: handleKeyboardNav,
	} = useKeyboardNavigation(visibleItems.length, (item) => {}, {
		listRef: dropdownMenuRef,
	});

	const handleKeyDown = (e) => {
		handleKeyboardNav(e, (index) => visibleItems[index]);
	};

	const toggleLabel = (() => {
		if (!selectedAddons.length) return readOnly ? "None" : "Select";
		const names = selectedAddons.map(
			(a) => a.name || a.addon_name || a.item_name || ""
		);
		return names.join(", ");
	})();

	if (readOnly) {
		return (
			<div className={`commonDropdownNoImage`}>
				<div className={`dropdownToggle`}>{toggleLabel}</div>
			</div>
		);
	}

	return (
		<div
			ref={dropdownRef}
			className={`commonDropdownNoImage`}
			tabIndex={0}
		>
			<div
				className={`dropdownToggle cursor-pointer`}
				onClick={() => {
					const newState = !isDropdownOpen;
					setDropdownOpen(newState);
					if (newState) {
						fetchAddons();
					}
					if (onDropdownToggle) {
						onDropdownToggle(newState);
					}
				}}
			>
				{toggleLabel}
			</div>
			{isDropdownOpen && (
				<div
					className={`${styles.dropdownContainer}`}
					ref={dropdownMenuRef}
				>
					<div className={styles.header}>
						<SearchBar
							ref={searchInputRef}
							placeholder="Search..."
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							onKeyDown={handleKeyDown}
							className="border-0"
						/>
					</div>
					<div className={`${styles.dropdownMenu}`}>
						{isLoading && (
							<div
								className={`${styles.dropdownItem} no-item-found-placeholder`}
							>
								Loading...
							</div>
						)}
						{!isLoading && apiError && (
							<div
								className={`${styles.dropdownItem} ${styles.infoRow}`}
							>
								{apiError}
							</div>
						)}
						{!isLoading &&
							!apiError &&
							(visibleItems.length > 0 ? (
								(() => {
									let globalIndex = 0;
									return filteredOptions.map((category, idx) => {
										return (
											<div
												key={idx}
												className={styles.dropdownItem}
											>
												<div className={styles.bottomList}>
													{(category?.items || category?.addons || []).map(
														(item, itemIdx) => {
															const currentIndex = globalIndex++;
															const isChecked = selectedAddons.some(
																(a) =>
																	(a.id ||
																		a.addon_id ||
																		a.item_id) ===
																	(item.id ||
																		item.addon_id ||
																		item.item_id)
															);

															return (
																<div
																	className={`${styles.item} checkbox hover`}
																	key={item.id || itemIdx}
																	data-key-nav-index={currentIndex}
																	onClick={() => {
																		// Toggle selection and propagate via onChange
																		if (!onChange) return;
																		const key =
																			item.id ||
																			item.addon_id ||
																			item.item_id;
																		const exists = selectedAddons.some(
																			(a) =>
																				(a.id ||
																					a.addon_id ||
																					a.item_id) === key
																		);
																		let next;
																		if (exists) {
																			next = selectedAddons.filter(
																				(a) =>
																					(a.id ||
																						a.addon_id ||
																						a.item_id) !== key
																			);
																		} else {
																			// Normalize to {id, name, quantity: 1} format
																			const normalizedAddon = {
																				id: Number(item.id ?? item.addon_id ?? item.item_id ?? 0),
																				name: item.name ?? item.addon_name ?? item.item_name ?? "",
																				quantity: 1,
																			};
																			next = [...selectedAddons, normalizedAddon];
																		}
																		onChange(next);
																	}}
																>
																	<div className={styles.left}>
																		<input
																			type="checkbox"
																			checked={isChecked}
																			readOnly
																			className={styles.hiddenCheckbox}
																		/>
																		<span className={`${styles.checkmark} checkmark icn check`} />
																		<span className={styles.label}>
																			{item.name}
																		</span>
																	</div>
																	<div className={styles.right}>
																		{(() => {
																			const n = getEffectiveAddonPrice(
																				item,
																				addonPricingContext
																			);
																			return Number.isFinite(n)
																				? `$${n.toFixed(2)}`
																				: "";
																		})()}
																	</div>
																</div>
															);
														}
													)}
												</div>
											</div>
										);
									});
								})()
							) : (
								<div
									className={`${styles.dropdownItem} no-item-found-placeholder`}
								>
									{emptyDropdownPlaceholder}
								</div>
							))}
					</div>
				</div>
			)}
		</div>
	);
};

export default ServiceAddons;
